import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { bannerTemplates, getBannerImageUrl } from "@/mocks/bannerTemplates";
import { DEFAULT_BUTTON_POSITION, OVERLAY_POSITION_CLASSES, isBelowPosition, normalizeButtonPosition } from "@/lib/bannerPosition";
import ButtonPositionSelector from "./ButtonPositionSelector";

interface ContactInfo {
  name?: string;
  whatsapp?: string;
  email?: string;
}

interface Banner {
  id: number;
  title: string;
  subtitle: string | null;
  button_text: string | null;
  button_link: string;
  button_position?: string | null;
  image_url: string;
  sort_order: number;
  active: boolean;
  category: string | null;
  contact_info: ContactInfo | null;
  created_at: string;
  member_id?: number | null;
  moderation_status?: string | null;
  reviewed_at?: string | null;
}

interface MemberOption {
  id: number;
  full_name: string;
  email: string | null;
}

interface Toast {
  id: number;
  type: "success" | "error";
  message: string;
}

type FilterTab = "all" | "pending" | "active" | "inactive";

const emptyForm = {
  title: "",
  subtitle: "",
  button_text: "Quero divulgar também",
  button_link: "/cadastro",
  button_position: DEFAULT_BUTTON_POSITION,
  image_url: "",
  sort_order: 0,
  active: true,
};

const BUTTON_TEXT_OPTIONS = [
  "Quero divulgar também",
  "Saiba mais",
  "Participar",
  "Quero me inscrever",
  "Ver detalhes",
  "Falar com a comunidade",
  "Acessar agora",
  "Entrar em contato",
  "__custom__",
];

const CATEGORY_LABELS: Record<string, string> = {
  business: "Divulgar negócio",
  job_offer: "Oferecer vaga",
  job_seek: "Procurar vaga",
  referral: "Pedir indicação",
  other: "Outro",
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result?.toString().split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function buildSiteBaseUrl(): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const pathPrefix = basePath ? `/${basePath}` : "";
  return `${window.location.origin}${pathPrefix}`;
}

function BannerPreview({ banner, onClose }: { banner: Banner; onClose: () => void }) {
  const pos = normalizeButtonPosition(banner.button_position);
  const overlayClass = OVERLAY_POSITION_CLASSES[pos];
  const isBelow = isBelowPosition(banner.button_position);
  const buttonNode = banner.button_text ? (
    <span className="font-body text-sm px-7 py-3 rounded-full bg-white text-brown tracking-wide uppercase whitespace-nowrap inline-flex items-center gap-2">
      {banner.button_text}
      <i className="ri-arrow-right-line text-base" />
    </span>
  ) : null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4 md:p-8">
      <div className="w-full max-w-5xl">
        <div className="flex items-center justify-between mb-3">
          <h5 className="font-display text-lg text-white">Pré-visualização do banner</h5>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="relative w-full h-[260px] md:h-[320px] overflow-hidden rounded-2xl">
          {/* Fundo */}
          <img
            src={banner.image_url}
            alt={banner.title}
            className="w-full h-full object-cover"
          />
          {/* Overlay */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(92,58,33,0.65) 0%, rgba(92,58,33,0.35) 50%, rgba(198,123,92,0.45) 100%)",
            }}
          />
          {/* Botão sobreposto (posição personalizada) */}
          {buttonNode && overlayClass && (
            <div className={`absolute ${overlayClass} z-10`}>{buttonNode}</div>
          )}
          {/* Conteúdo */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 md:px-10 lg:px-16">
            <h3 className="font-display italic tracking-tight text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white mb-3 md:mb-4 max-w-3xl leading-tight">
              {banner.title}
            </h3>
            {banner.subtitle && (
              <p className="font-body text-sm md:text-base text-white/80 mb-5 md:mb-6 max-w-xl leading-relaxed">
                {banner.subtitle}
              </p>
            )}
            {buttonNode && !overlayClass && !isBelow && (
              <div className="mt-5 md:mt-6">{buttonNode}</div>
            )}
          </div>
          {/* Rodapé — quem oferece */}
          {banner.contact_info?.name && (
            <div className="absolute bottom-10 md:bottom-12 right-4 md:right-8 z-10 flex justify-end">
              <span className="font-body text-xs text-white/70 bg-black/20 backdrop-blur-sm px-4 py-1.5 rounded-full">
                Oportunidade oferecida por: <strong className="text-white">{banner.contact_info.name}</strong>
              </span>
            </div>
          )}
        </div>
        {buttonNode && isBelow && (
          <div className="flex justify-center mt-3">{buttonNode}</div>
        )}
        <p className="font-body text-sm text-white/60 text-center mt-3">
          Assim o banner aparecerá na página inicial da comunidade
        </p>
      </div>
    </div>
  );
}

export default function BannerManager() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
  const [useCustomButtonText, setUseCustomButtonText] = useState(false);
  const [previewBanner, setPreviewBanner] = useState<Banner | null>(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [memberNames, setMemberNames] = useState<Record<string, string>>({});
  const [memberEmails, setMemberEmails] = useState<Record<string, string>>({});
  const [memberSearch, setMemberSearch] = useState("");
  const [memberResults, setMemberResults] = useState<MemberOption[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(null);
  const [memberSearching, setMemberSearching] = useState(false);
  const [memberWarning, setMemberWarning] = useState("");
  const [notifyMember, setNotifyMember] = useState(false);
  const memberSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  let toastCounter = 0;
  function showToast(type: "success" | "error", message: string) {
    const id = Date.now() + toastCounter++;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 6000);
  }

  useEffect(() => {
    loadBanners();
  }, []);

  useEffect(() => {
    if (showForm && formRef.current) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [scrollTrigger]);

  async function loadBanners() {
    setLoading(true);
    const { data, error } = await supabase
      .from("banners")
      .select("*")
      .order("sort_order", { ascending: true });
    if (!error && data) {
      const bannerList = data as Banner[];
      setBanners(bannerList);

      const memberIds = [...new Set(
        bannerList.filter((b) => b.member_id).map((b) => b.member_id as number)
      )];
      if (memberIds.length > 0) {
        const { data: members, error: mErr } = await supabase
          .from("members")
          .select("id, full_name, email")
          .in("id", memberIds);
        if (!mErr && members) {
          const nameMap: Record<string, string> = {};
          const emailMap: Record<string, string> = {};
          members.forEach((m: { id: string | number; full_name: string; email?: string | null }) => {
            nameMap[String(m.id)] = m.full_name;
            if (m.email) emailMap[String(m.id)] = m.email;
          });
          setMemberNames(nameMap);
          setMemberEmails(emailMap);
        }
      }
    }
    setLoading(false);
  }

  function clearMember() {
    setSelectedMember(null);
    setMemberSearch("");
    setMemberResults([]);
    setNotifyMember(false);
    setMemberWarning("");
  }

  function handleMemberSearchChange(value: string) {
    setMemberSearch(value);
    if (memberSearchTimer.current) clearTimeout(memberSearchTimer.current);
    const term = value.trim().replace(/[%_,]/g, "");
    if (!term) {
      setMemberResults([]);
      return;
    }
    memberSearchTimer.current = setTimeout(async () => {
      setMemberSearching(true);
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name, email")
        .in("status", ["approved", "active"])
        .or(`full_name.ilike.%${term}%,email.ilike.%${term}%`)
        .order("full_name", { ascending: true })
        .limit(8);
      setMemberSearching(false);
      setMemberResults(!error && data ? (data as MemberOption[]) : []);
    }, 300);
  }

  function selectMember(member: MemberOption) {
    setSelectedMember(member);
    setMemberSearch("");
    setMemberResults([]);
    setNotifyMember(false);
    setMemberWarning("");
    checkMemberActiveCount(member.id, editId ?? null);
  }

  async function checkMemberActiveCount(memberId: number, excludeId?: number | null) {
    let query = supabase
      .from("banners")
      .select("id")
      .eq("member_id", memberId)
      .eq("active", true);
    if (excludeId) query = query.neq("id", excludeId);
    const { data, error } = await query;
    if (!error && data && (data as { id: number }[]).length >= 3) {
      setMemberWarning(
        "Esta membro já tem 3 anúncios ativos. Você ainda pode publicar, mas ela ficará acima do limite da comunidade.",
      );
    } else {
      setMemberWarning("");
    }
  }

  function notifySelectedMember(bannerTitle: string) {
    if (!notifyMember || !selectedMember || !selectedMember.email) return;
    const baseUrl = buildSiteBaseUrl();
    supabase.functions
      .invoke("notify-banner-approved", {
        body: {
          memberName: selectedMember.full_name || "Integrante",
          memberEmail: selectedMember.email,
          bannerTitle,
          baseUrl,
        },
      })
      .then(({ error: fnError }) => {
        if (fnError) console.error("Falha ao notificar membro (anúncio no ar):", fnError);
      });
  }

  function openCreate() {
    setForm(emptyForm);
    setIsEditing(false);
    setEditId(null);
    setShowForm(true);
    setUseCustomButtonText(false);
    clearMember();
    setScrollTrigger((prev) => prev + 1);
  }

  function openEdit(banner: Banner) {
    const btnText = banner.button_text || "";
    const isPreset = BUTTON_TEXT_OPTIONS.slice(0, -1).includes(btnText);
    setForm({
      title: banner.title,
      subtitle: banner.subtitle || "",
      button_text: btnText,
      button_link: banner.button_link,
      button_position: normalizeButtonPosition(banner.button_position),
      image_url: banner.image_url,
      sort_order: banner.sort_order,
      active: banner.active,
    });
    setIsEditing(true);
    setEditId(banner.id);
    setShowForm(true);
    setUseCustomButtonText(!isPreset && btnText !== "");
    setMemberSearch("");
    setMemberResults([]);
    setNotifyMember(false);
    if (banner.member_id) {
      const id = banner.member_id;
      const name = memberNames[String(id)] || "";
      const email = memberEmails[String(id)] || "";
      setSelectedMember({ id, full_name: name, email: email || null });
      setMemberWarning("");
      checkMemberActiveCount(id, banner.id);
    } else {
      setSelectedMember(null);
      setMemberWarning("");
    }
    setScrollTrigger((prev) => prev + 1);
  }

  function closeForm() {
    setShowForm(false);
    setIsEditing(false);
    setEditId(null);
    setForm(emptyForm);
    setUseCustomButtonText(false);
    clearMember();
  }

  function openPreviewFromForm() {
    if (!form.image_url.trim()) {
      showToast("error", "Selecione uma imagem ou template antes de pré-visualizar.");
      return;
    }
    const tempBanner: Banner = {
      id: 0,
      title: form.title || "Título do banner",
      subtitle: form.subtitle || null,
      button_text: form.button_text || null,
      button_link: form.button_link,
      button_position: form.button_position,
      image_url: form.image_url,
      sort_order: form.sort_order,
      active: form.active,
      category: null,
      contact_info: null,
      created_at: "",
    };
    setPreviewBanner(tempBanner);
  }

  async function uploadImage(file: File): Promise<string | null> {
    if (!file) return null;
    try {
      setUploading(true);
      const base64 = await fileToBase64(file);
      const { data, error: fnError } = await supabase.functions.invoke("upload-banner-image", {
        body: {
          filename: file.name,
          mimeType: file.type,
          base64Data: base64,
        },
      });
      if (fnError || !data || !data.success) {
        showToast("error", "Falha ao fazer upload da imagem.");
        return null;
      }
      return data.url;
    } catch {
      showToast("error", "Erro ao enviar imagem.");
      return null;
    } finally {
      setUploading(false);
    }
  }

  async function handleMainImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) {
      setForm((f) => ({ ...f, image_url: url }));
      showToast("success", "Imagem enviada com sucesso!");
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) {
      showToast("error", "O título do banner é obrigatório.");
      return;
    }
    if (!form.image_url.trim()) {
      showToast("error", "A imagem do banner é obrigatória.");
      return;
    }

    const originalBanner = isEditing && editId ? banners.find((b) => b.id === editId) : null;
    const memberChanged = selectedMember?.id !== (originalBanner?.member_id ?? null);

    const payload = {
      title: form.title.trim(),
      subtitle: form.subtitle.trim() || null,
      button_text: form.button_text.trim() || null,
      button_link: form.button_link.trim() || "#",
      button_position: normalizeButtonPosition(form.button_position),
      image_url: form.image_url.trim(),
      sort_order: form.sort_order,
      active: form.active,
      moderation_status: "approved",
      member_id: selectedMember ? selectedMember.id : null,
      ...(memberChanged
        ? {
            contact_info: selectedMember
              ? { name: selectedMember.full_name, email: selectedMember.email || null }
              : null,
          }
        : {}),
    };

    const savedTitle = form.title.trim();

    if (isEditing && editId) {
      const { error } = await supabase
        .from("banners")
        .update(payload)
        .eq("id", editId);
      if (!error) {
        showToast("success", "Banner atualizado com sucesso!");
        notifySelectedMember(savedTitle);
        loadBanners();
        closeForm();
      } else {
        showToast("error", "Erro ao atualizar banner: " + error.message);
      }
    } else {
      const { error } = await supabase
        .from("banners")
        .insert(payload);
      if (!error) {
        showToast("success", "Banner criado com sucesso!");
        notifySelectedMember(savedTitle);
        loadBanners();
        closeForm();
      } else {
        showToast("error", "Erro ao criar banner: " + error.message);
      }
    }
  }

  function sendMemberBannerEmail(banner: Banner, action: "approved" | "rejected") {
    if (!banner.member_id) return;
    const memberEmail = memberEmails[String(banner.member_id)];
    if (!memberEmail) return;
    const memberName = memberNames[String(banner.member_id)] || "Integrante";
    const baseUrl = buildSiteBaseUrl();

    supabase.functions
      .invoke(action === "approved" ? "notify-banner-approved" : "notify-banner-rejected", {
        body: {
          memberName,
          memberEmail,
          bannerTitle: banner.title,
          baseUrl,
        },
      })
      .then(({ error: fnError }) => {
        if (fnError) console.error(`Falha ao notificar membro (${action}):`, fnError);
      });
  }

  async function approveBanner(id: number) {
    const { error } = await supabase
      .from("banners")
      .update({
        active: true,
        moderation_status: "approved",
        reviewed_at: new Date().toISOString(),
        approval_token: null,
      })
      .eq("id", id);
    if (!error) {
      setBanners((prev) =>
        prev.map((b) =>
          b.id === id
            ? { ...b, active: true, moderation_status: "approved", reviewed_at: new Date().toISOString(), approval_token: null }
            : b
        )
      );
      showToast("success", "Banner aprovado! Agora está visível no carrossel.");
      const banner = banners.find((b) => b.id === id);
      if (banner) sendMemberBannerEmail(banner, "approved");
    } else {
      showToast("error", "Erro ao aprovar banner.");
    }
  }

  async function rejectBanner(id: number) {
    const { error } = await supabase
      .from("banners")
      .update({
        active: false,
        moderation_status: "rejected",
        reviewed_at: new Date().toISOString(),
        approval_token: null,
      })
      .eq("id", id);
    if (!error) {
      setBanners((prev) =>
        prev.map((b) =>
          b.id === id
            ? { ...b, active: false, moderation_status: "rejected", reviewed_at: new Date().toISOString(), approval_token: null }
            : b
        )
      );
      showToast("success", "Banner recusado.");
      const banner = banners.find((b) => b.id === id);
      if (banner) sendMemberBannerEmail(banner, "rejected");
    } else {
      showToast("error", "Erro ao recusar banner.");
    }
  }

  async function toggleStatus(id: number, current: boolean) {
    const newStatus = !current;
    const updatePayload: Record<string, unknown> = { active: newStatus };
    if (newStatus) updatePayload.moderation_status = "approved";
    const { error } = await supabase
      .from("banners")
      .update(updatePayload)
      .eq("id", id);
    if (!error) {
      setBanners((prev) =>
        prev.map((b) => {
          if (b.id !== id) return b;
          const next: Banner = { ...b, active: newStatus };
          if (newStatus) next.moderation_status = "approved";
          return next;
        })
      );
      showToast("success", `Banner ${newStatus ? "ativado" : "desativado"}.`);
    }
  }

  async function deleteBanner(id: number) {
    if (!confirm("Tem certeza que deseja excluir este banner permanentemente?")) return;
    const { error } = await supabase.from("banners").delete().eq("id", id);
    if (!error) {
      setBanners((prev) => prev.filter((b) => b.id !== id));
      showToast("success", "Banner excluído.");
    } else {
      showToast("error", "Erro ao excluir banner.");
    }
  }

  function isCommunitySubmitted(banner: Banner): boolean {
    return banner.category !== null || banner.contact_info !== null;
  }

  function isPending(banner: Banner): boolean {
    return banner.moderation_status === "pending";
  }

  const pending = banners.filter((b) => isPending(b));
  const activeList = banners.filter((b) => b.active);
  const inactiveList = banners.filter((b) => !b.active && !isPending(b));

  const filteredBanners = activeFilter === "all"
    ? banners
    : activeFilter === "pending"
      ? pending
      : activeFilter === "active"
        ? activeList
        : inactiveList;

  const filterTabs: { key: FilterTab; label: string; count: number; icon: string; colorClass: string }[] = [
    { key: "all", label: "Todos", count: banners.length, icon: "ri-image-line", colorClass: "text-brown" },
    { key: "pending", label: "Pendentes", count: pending.length, icon: "ri-time-line", colorClass: "text-yellow-600" },
    { key: "active", label: "Ativos", count: activeList.length, icon: "ri-checkbox-circle-line", colorClass: "text-green-600" },
    { key: "inactive", label: "Inativos", count: inactiveList.length, icon: "ri-eye-off-line", colorClass: "text-brown/40" },
  ];

  return (
    <div>
      {/* Preview modal */}
      {previewBanner && (
        <BannerPreview
          banner={previewBanner}
          onClose={() => setPreviewBanner(null)}
        />
      )}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed top-20 right-6 z-50 flex flex-col gap-2 max-w-sm">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`px-4 py-3 rounded-xl shadow-lg font-body text-sm flex items-start gap-2.5 animate-slide-in ${
                t.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              <i
                className={`${
                  t.type === "success"
                    ? "ri-checkbox-circle-line text-green-500"
                    : "ri-error-warning-line text-red-500"
                } mt-0.5`}
              />
              <span className="flex-1">{t.message}</span>
              <button
                onClick={() =>
                  setToasts((prev) => prev.filter((x) => x.id !== t.id))
                }
                className="text-brown/30 hover:text-brown/60 cursor-pointer shrink-0"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Uploading overlay */}
      {uploading && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <p className="font-body text-sm text-brown">Enviando imagem...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-display text-xl text-brown">Banners do Carrossel</h3>
          <p className="font-body text-sm text-brown/60 mt-1">
            Gerencie os banners de propaganda que aparecem na página inicial
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" /> Novo banner
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-6">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeFilter === tab.key
                ? "bg-primary text-white shadow-md"
                : "bg-white text-brown hover:bg-beige border border-nude/60"
            }`}
          >
            <i className={`${tab.icon} ${activeFilter === tab.key ? "text-white" : tab.colorClass}`} />
            <span>{tab.label}</span>
            <span className={`ml-1 font-display text-base ${
              activeFilter === tab.key ? "text-white/80" : "text-brown/40"
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <div ref={formRef} className="bg-white rounded-2xl p-6 mb-6">
          <h4 className="font-display text-lg text-brown mb-4">
            {isEditing ? "Editar Banner" : "Novo Banner"}
          </h4>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Título *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                  className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder='Ex: "Divulgue seu negócio aqui"'
                />
              </div>
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Texto do botão
                </label>
                {!useCustomButtonText ? (
                  <select
                    value={BUTTON_TEXT_OPTIONS.slice(0, -1).includes(form.button_text) ? form.button_text : "__custom__"}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "__custom__") {
                        setUseCustomButtonText(true);
                        setForm((f) => ({ ...f, button_text: "" }));
                      } else {
                        setForm((f) => ({ ...f, button_text: val }));
                      }
                    }}
                    className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors cursor-pointer"
                  >
                    {BUTTON_TEXT_OPTIONS.map((opt) =>
                      opt === "__custom__" ? (
                        <option key={opt} value="__custom__">
                          ✏️ Outro (personalizado)...
                        </option>
                      ) : (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ),
                    )}
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.button_text}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, button_text: e.target.value }))
                      }
                      className="flex-1 px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                      placeholder="Digite o texto do botão..."
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setUseCustomButtonText(false);
                        setForm((f) => ({ ...f, button_text: BUTTON_TEXT_OPTIONS[0] }));
                      }}
                      className="px-3 py-2.5 rounded-lg bg-beige border border-nude text-brown/60 font-body text-xs hover:bg-nude/30 transition-colors whitespace-nowrap cursor-pointer"
                      title="Voltar para opções prontas"
                    >
                      <i className="ri-arrow-go-back-line" />
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Link do botão
                </label>
                <input
                  type="text"
                  value={form.button_link}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, button_link: e.target.value }))
                  }
                  className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder='Ex: "/cadastro"'
                />
              </div>
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Ordem
                </label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      sort_order: parseInt(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>

            {/* Posição do botão */}
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Posição do botão
              </label>
              <ButtonPositionSelector
                value={form.button_position}
                onChange={(val) => setForm((f) => ({ ...f, button_position: val }))}
              />
            </div>

            {/* Subtitle */}
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Subtítulo
              </label>
              <textarea
                value={form.subtitle}
                onChange={(e) =>
                  setForm((f) => ({ ...f, subtitle: e.target.value }))
                }
                rows={2}
                maxLength={200}
                className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors resize-none"
                placeholder="Texto descritivo que aparece abaixo do título..."
              />
              <p className="font-body text-[11px] text-brown/40 mt-1 text-right">
                {form.subtitle.length}/200
              </p>
            </div>

            {/* Image */}
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Imagem do Banner *
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    value={form.image_url}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, image_url: e.target.value }))
                    }
                    className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                    placeholder="URL da imagem..."
                  />
                </div>
                <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary/10 text-primary font-body text-sm hover:bg-primary/20 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-upload-cloud-line" />
                  <span>Enviar arquivo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    className="hidden"
                  />
                </label>
              </div>
              {form.image_url && (
                <div className="mt-3 w-full aspect-[1440/400] max-w-md rounded-xl overflow-hidden">
                  <img
                    src={form.image_url}
                    alt="Preview do banner"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Templates sempre visíveis */}
              <div className="mt-5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-image-line text-primary" />
                  <span className="font-body text-sm font-medium text-brown">
                    Escolha um fundo para o banner
                  </span>
                  <span className="font-body text-xs text-brown/40">
                    — templates prontos do Entre Elas
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {bannerTemplates.map((tpl) => {
                    const imageUrl = getBannerImageUrl(tpl);
                    const isSelected = form.image_url === imageUrl;
                    const isPalette = !!tpl.palette;

                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => {
                          setForm((f) => ({ ...f, image_url: imageUrl }));
                          showToast("success", `Template "${tpl.label}" selecionado!`);
                        }}
                        className={`group relative rounded-xl overflow-hidden aspect-[1440/400] border-2 transition-all duration-200 cursor-pointer ${
                          isSelected
                            ? "border-primary ring-2 ring-primary/30"
                            : "border-nude hover:border-primary/50"
                        }`}
                      >
                        {isPalette && tpl.palette ? (
                          <>
                            {/* Gradient background representing the palette */}
                            <div
                              className="w-full h-full"
                              style={{
                                background: `linear-gradient(135deg, ${tpl.palette.primary} 0%, ${tpl.palette.accent} 50%, ${tpl.palette.background} 100%)`,
                              }}
                            />
                            {/* Color swatches at bottom */}
                            <div className="absolute bottom-0 left-0 right-0 p-2 flex items-center gap-1.5 bg-gradient-to-t from-black/40 to-transparent">
                              <span
                                className="w-3 h-3 rounded-full border border-white/40"
                                style={{ backgroundColor: tpl.palette.primary }}
                                title="Cor principal"
                              />
                              <span
                                className="w-3 h-3 rounded-full border border-white/40"
                                style={{ backgroundColor: tpl.palette.accent }}
                                title="Cor de destaque"
                              />
                              <span
                                className="w-3 h-3 rounded-full border border-white/40"
                                style={{ backgroundColor: tpl.palette.background }}
                                title="Cor de fundo"
                              />
                              <span className="font-body text-[10px] text-white ml-1.5 truncate">
                                {tpl.label}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <img
                              src={tpl.image_url}
                              alt={tpl.label}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                              <span className="font-body text-[10px] text-white block truncate">
                                {tpl.label}
                              </span>
                            </div>
                          </>
                        )}
                        {isSelected && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-primary text-white">
                            <i className="ri-check-line text-xs" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {!form.image_url && (
                  <p className="font-body text-xs text-yellow-600 mt-2 flex items-center gap-1.5">
                    <i className="ri-alert-line" />
                    Selecione um template acima ou envie uma imagem para o banner ter fundo.
                  </p>
                )}
              </div>
            </div>

            {/* Publicar em nome de */}
            <div className="border-t border-nude pt-4">
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Publicar em nome de{" "}
                <span className="text-brown/40 normal-case font-normal">(opcional)</span>
              </label>
              <p className="font-body text-xs text-brown/50 mb-3 leading-relaxed">
                Selecione uma membro para o anúncio aparecer como sendo dela, com link no
                perfil e listado em "Meus anúncios". Sem seleção, o anúncio continua
                institucional.
              </p>

              {selectedMember ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-body text-sm">
                    <i className="ri-user-line" />
                    {selectedMember.full_name || "Membro selecionado"}
                    {selectedMember.email && (
                      <span className="text-primary/60 text-xs">({selectedMember.email})</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={clearMember}
                    className="px-3 py-2 rounded-full bg-white border border-nude text-brown/60 font-body text-xs hover:bg-nude/40 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-close-line mr-1" />
                    Remover
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => handleMemberSearchChange(e.target.value)}
                    placeholder="Buscar membro por nome ou e-mail..."
                    className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  />
                  {memberSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    </div>
                  )}
                  {memberResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-nude rounded-xl overflow-hidden max-h-60 overflow-y-auto">
                      {memberResults.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => selectMember(m)}
                          className="w-full text-left px-4 py-2.5 font-body text-sm text-brown hover:bg-beige/60 transition-colors cursor-pointer flex items-center gap-2"
                        >
                          <i className="ri-user-line text-brown/40" />
                          <span className="flex-1 min-w-0">
                            <span className="block truncate">{m.full_name}</span>
                            {m.email && (
                              <span className="block text-xs text-brown/50 truncate">{m.email}</span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {memberSearch.trim() && !memberSearching && memberResults.length === 0 && (
                    <p className="font-body text-xs text-brown/40 mt-1.5">
                      Nenhuma membro aprovada encontrada.
                    </p>
                  )}
                </div>
              )}

              {selectedMember && memberWarning && (
                <div className="mt-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
                  <i className="ri-alert-line text-amber-600 mt-0.5 shrink-0" />
                  <span className="font-body text-xs text-amber-800 leading-relaxed">
                    {memberWarning}
                  </span>
                </div>
              )}

              {selectedMember && (
                <label className="mt-3 flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifyMember}
                    onChange={(e) => setNotifyMember(e.target.checked)}
                    disabled={!selectedMember.email}
                    className="mt-0.5 w-4 h-4 rounded border-nude text-primary focus:ring-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className="font-body text-sm text-brown">
                    Avisar a membro por e-mail que o anúncio está no ar
                    {!selectedMember.email && (
                      <span className="block text-xs text-brown/40 mt-0.5">
                        Esta membro não possui e-mail cadastrado.
                      </span>
                    )}
                  </span>
                </label>
              )}
            </div>

            {/* Active toggle */}
            <div className="flex items-center gap-3">
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide">
                Ativo no site
              </label>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, active: !f.active }))}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer ${
                  form.active ? "bg-primary" : "bg-nude"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                    form.active ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
              <span className="font-body text-sm text-brown">
                {form.active ? "Sim — aparece no carrossel" : "Não — oculto"}
              </span>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="submit"
                className="px-6 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                {isEditing ? "Salvar alterações" : "Criar banner"}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="px-6 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={openPreviewFromForm}
                className="px-6 py-2.5 rounded-full bg-beige border border-nude text-brown font-body text-sm hover:bg-nude/40 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-eye-line" />
                Ver como vai ficar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-3" />
          <p className="font-body text-sm text-brown/60">Carregando banners...</p>
        </div>
      ) : filteredBanners.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center">
          <i className="ri-image-line text-4xl text-brown/20 mb-3" />
          <p className="font-body text-brown/60">
            {activeFilter === "pending"
              ? "Nenhum banner pendente de aprovação."
              : activeFilter === "active"
                ? "Nenhum banner ativo no momento."
                : "Nenhum banner cadastrado."}
          </p>
          <button
            onClick={openCreate}
            className="mt-4 text-primary font-body text-sm hover:underline cursor-pointer"
          >
            Criar primeiro banner
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBanners.map((banner) => {
            const community = isCommunitySubmitted(banner);
            const pendingB = isPending(banner);

            return (
              <div
                key={banner.id}
                className={`bg-white rounded-2xl p-4 md:p-5 flex flex-col md:flex-row gap-4 md:gap-5 items-start ${
                  pendingB ? "ring-2 ring-yellow-300" : ""
                }`}
              >
                {/* Thumbnail */}
                <div className="w-full md:w-44 shrink-0 rounded-xl overflow-hidden aspect-[1440/400]">
                  <img
                    src={banner.image_url}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="font-display text-base text-brown">
                      {banner.title}
                    </h4>

                    {/* Status badge */}
                    {pendingB && (
                      <span className="px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-body text-[11px] uppercase tracking-wide">
                        Pendente
                      </span>
                    )}
                    {!pendingB && banner.moderation_status === "rejected" && (
                      <span className="px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 font-body text-[11px] uppercase tracking-wide">
                        Rejeitado
                      </span>
                    )}
                    {!pendingB && banner.moderation_status !== "rejected" && (
                      <span
                        className={`px-2.5 py-0.5 rounded-full font-body text-[11px] uppercase tracking-wide ${
                          banner.active
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {banner.active ? "Ativo" : "Inativo"}
                      </span>
                    )}

                    {/* Category badge */}
                    {banner.category && CATEGORY_LABELS[banner.category] && (
                      <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-body text-[11px]">
                        <i className="ri-price-tag-3-line mr-1" />
                        {CATEGORY_LABELS[banner.category]}
                      </span>
                    )}

                    {/* Origin badge */}
                    {community && (
                      <span className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary font-body text-[11px]">
                        <i className="ri-group-line mr-1" />
                        Comunidade
                      </span>
                    )}

                    {/* Publisher badge */}
                    {banner.member_id && memberNames[String(banner.member_id)] && (
                      <span className="px-2.5 py-0.5 rounded-full bg-beige font-body text-[11px] text-brown/70">
                        <i className="ri-user-line mr-1" />
                        Publicado por: {memberNames[String(banner.member_id)]}
                      </span>
                    )}

                    <span className="px-2 py-0.5 rounded-full bg-beige font-body text-[11px] text-brown/50">
                      Ordem {banner.sort_order}
                    </span>
                  </div>

                  {banner.subtitle && (
                    <p className="font-body text-sm text-brown/60 mb-2 line-clamp-2">
                      {banner.subtitle}
                    </p>
                  )}

                  {/* Contact info for community banners */}
                  {banner.contact_info && (
                    <div className="mb-2 p-3 rounded-xl bg-beige/60">
                      <p className="font-body text-[11px] text-brown/40 uppercase tracking-wide mb-1.5">
                        Dados de contato
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-body">
                        {banner.contact_info.name && (
                          <span className="flex items-center gap-1 text-brown">
                            <i className="ri-user-line text-brown/40" />
                            {banner.contact_info.name}
                          </span>
                        )}
                        {banner.contact_info.whatsapp && (
                          <span className="flex items-center gap-1 text-brown">
                            <i className="ri-whatsapp-line text-brown/40" />
                            {banner.contact_info.whatsapp}
                          </span>
                        )}
                        {banner.contact_info.email && (
                          <span className="flex items-center gap-1 text-brown">
                            <i className="ri-mail-line text-brown/40" />
                            {banner.contact_info.email}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 text-xs font-body text-brown/50">
                    {banner.button_text && (
                      <span className="flex items-center gap-1">
                        <i className="ri-link" /> Botão: "{banner.button_text}"
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <i className="ri-arrow-right-line" />{" "}
                      {banner.button_link}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line" />{" "}
                      {new Date(banner.created_at).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 shrink-0">
                  {pendingB && (
                    <button
                      onClick={() => approveBanner(banner.id)}
                      className="px-3 py-2 rounded-lg bg-primary text-white font-body text-xs hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-check-line mr-1" /> Aprovar
                    </button>
                  )}
                  {pendingB && (
                    <button
                      onClick={() => rejectBanner(banner.id)}
                      className="px-3 py-2 rounded-lg bg-white border border-red-200 text-red-500 font-body text-xs hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-close-line mr-1" /> Rejeitar
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(banner)}
                    className="px-3 py-2 rounded-lg bg-primary/10 text-primary font-body text-xs hover:bg-primary/20 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-1" /> Editar
                  </button>
                  <button
                    onClick={() => setPreviewBanner(banner)}
                    className="px-3 py-2 rounded-lg bg-beige border border-nude text-brown font-body text-xs hover:bg-nude/40 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-1"
                  >
                    <i className="ri-eye-line" />
                    Pré-visualizar
                  </button>
                  {!pendingB && (
                    <button
                      onClick={() => toggleStatus(banner.id, banner.active)}
                      className={`px-3 py-2 rounded-lg font-body text-xs transition-colors whitespace-nowrap cursor-pointer ${
                        banner.active
                          ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                          : "bg-green-100 text-green-700 hover:bg-green-200"
                      }`}
                    >
                      {banner.active ? "Desativar" : "Ativar"}
                    </button>
                  )}
                  <button
                    onClick={() => deleteBanner(banner.id)}
                    className="px-3 py-2 rounded-lg bg-white border border-nude text-red-500 font-body text-xs hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}